import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import { ApiResponse, PaginatedResponse } from "@/shared/types";
import {
  ClanApplication,
  Clan,
  ClanMember,
  ClanMemberStatus,
  ClanProfile,
  ClanRole,
} from "../model";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import {
  AddClanContactRequest,
  ApproveClanApplicationRequest,
  CreateClanProfileRequest,
  CreateClanRequest,
  EditClanContactRequest,
  EditClanRequest,
  GetClanApplicationsRequest,
  GetClanMembersRequest,
  GetClanProfilesRequest,
  GetClansRequest,
  InviteToClanRequest,
  KickClanMemberRequest,
  RejectClanApplicationRequest,
  StartClanRecruitmentRequest,
  StopClanRecruitmentRequest,
} from "./api.dto";
import { useAuthUser } from "@/shared/hooks";

export const useClans = (
  params: GetClansRequest,
  { enabled }: { enabled?: boolean } = {},
) => {
  return useQuery({
    queryKey: ["clans", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<Clan>>(
        "/clans",
        filterObjectKeys(params),
        true,
      );
      return response.data;
    },
    enabled,
  });
};

export const useClanApplications = (
  params: GetClanApplicationsRequest,
  { enabled }: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedResponse<ClanApplication>>({
    queryKey: ["clanApplications", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<
        PaginatedResponse<ClanApplication>
      >("/clans/applications", filterObjectKeys(params), true);
      return response.data;
    },
    enabled,
  });
};

export const useApproveClanApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["approveClanApplication"],
    mutationFn: async (data: ApproveClanApplicationRequest) => {
      return await apiInstance.post(
        `/clans/applications/${data.applicationId}/approve`,
        undefined,
        undefined,
        true,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clanApplications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clanMembers"],
      });
    },
  });
};

export const useRejectClanApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["rejectClanApplication"],
    mutationFn: async (data: RejectClanApplicationRequest) => {
      return await apiInstance.post(
        `/clans/applications/${data.applicationId}/reject`,
        undefined,
        undefined,
        true,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clanApplications"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clanMembers"],
      });
    },
  });
};

export const useClansProfiles = (
  params: GetClanProfilesRequest,
  { enabled }: { enabled?: boolean } = {},
) => {
  return useQuery<PaginatedResponse<ClanProfile>>({
    queryKey: ["clansProfiles", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<ClanProfile>>(
        "/clans/profiles",
        filterObjectKeys(params),
        true,
      );
      return response.data;
    },
    enabled,
  });
};

export const useClansProfilesInf = (
  params: Omit<GetClanProfilesRequest, "currentPage">,
) => {
  return useInfiniteQuery<PaginatedResponse<ClanProfile>>({
    queryKey: ["clansProfilesInf", filterObjectKeys(params)],
    initialPageParam: 1,

    queryFn: async ({ pageParam }) => {
      const response = await apiInstance.get<PaginatedResponse<ClanProfile>>(
        "/clans/profiles",
        {
          ...filterObjectKeys(params),
          currentPage: pageParam,
        },
        true,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage.total;
      const itemsPerPage = params.pageSize;
      const currentPage = allPages.length;

      const totalPages = Math.ceil(totalItems / itemsPerPage);
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useCreateClanProfile = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Анкета клана успешно создана.",
      },
    },
    refetchQueries: ["clansProfiles", "clansProfilesInf", "clanMembers"],
  });

  return useMutation({
    mutationKey: ["createClanProfile"],
    mutationFn: async (data: CreateClanProfileRequest) => {
      const formData = new FormData();
      formData.append("description", data.description);
      formData.append("image", data.image);
      const response = await apiInstance.post(
        `/clans/${data.clanId}/profiles`,
        formData,
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useEditClanProfile = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Анкета клана успешно обновлена.",
      },
    },
    refetchQueries: ["clansProfiles", "clansProfilesInf", "clanMembers"],
  });

  return useMutation({
    mutationKey: ["editClanProfile"],
    mutationFn: async (data: CreateClanProfileRequest) => {
      const formData = new FormData();
      formData.append("description", data.description);
      formData.append("image", data.image);
      const response = await apiInstance.patch(
        `/clans/${data.clanId}/profiles/presentation`,
        formData,
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useInviteLeaderToClan = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Лидер приглашен в клан.",
      },
    },
    refetchQueries: ["clans"],
  });

  return useMutation({
    mutationKey: ["inviteLeaderToClan"],
    mutationFn: async (data: InviteToClanRequest) => {
      const response = await apiInstance.post(
        `/clans/${data.clanId}/invites`,
        {
          workerUsername: data.workerUsername,
          role: ClanRole.LEADER,
        },
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useInviteMemberToClan = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Участник приглашен в клан.",
      },
    },
    refetchQueries: ["clans"],
  });

  return useMutation({
    mutationKey: ["inviteMemberToClan"],
    mutationFn: async (data: InviteToClanRequest) => {
      const response = await apiInstance.post(
        `/clans/${data.clanId}/invites`,
        {
          workerUsername: data.workerUsername,
          role: ClanRole.MEMBER,
        },
        undefined,
        true,
      );
      return response.data;
    },
    onSuccess,
    onError,
  });
};

export const useStopClanRecruitment = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Набор в клан приостановлен.",
      },
    },
    refetchQueries: ["clans"],
  });

  return useMutation({
    mutationKey: ["stopClanRecruitment"],
    mutationFn: async (data: StopClanRecruitmentRequest) => {
      return await apiInstance.post(
        `/clans/${data.clanId}/recruitment/stop`,
        undefined,
        undefined,
        true,
      );
    },
    onSuccess,
    onError,
  });
};

export const useStartClanRecruitment = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Набор в клан возобновлен.",
      },
    },
    refetchQueries: ["clans"],
  });

  return useMutation({
    mutationKey: ["startClanRecruitment"],
    mutationFn: async (data: StartClanRecruitmentRequest) => {
      return await apiInstance.post(
        `/clans/${data.clanId}/recruitment/start`,
        undefined,
        undefined,
        true,
      );
    },
    onSuccess,
    onError,
  });
};

export const useAddClanContact = () => {
  return useMutation({
    mutationKey: ["addClanContact"],
    mutationFn: async (data: AddClanContactRequest) => {
      return await apiInstance.post(
        `/clans/${data.clanId}/contacts`,
        {
          main: data.main,
          reserve: data.reserve,
        },
        undefined,
        true,
      );
    },
  });
};

export const useEditClanContact = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Контакты клана успешно изменены.",
      },
    },
    refetchQueries: ["clans"],
  });

  return useMutation({
    mutationKey: ["editClanContact"],
    mutationFn: async (data: EditClanContactRequest) => {
      return await apiInstance.patch(
        `/clans/${data.clanId}/contacts`,
        {
          main: data.main,
          reserve: data.reserve,
        },
        undefined,
        true,
      );
    },
    onSuccess,
    onError,
  });
};

export const useClanMembers = (
  params: GetClanMembersRequest,
  { enabled }: { enabled?: boolean },
) => {
  return useQuery<PaginatedResponse<ClanMember>>({
    queryKey: ["clanMembers", filterObjectKeys(params)],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<ClanMember>>(
        `/clans/members`,
        filterObjectKeys(params),
        true,
      );
      return response.data;
    },
    enabled,
  });
};

export const useMyClanId = () => {
  const user = useAuthUser();

  const { data: clanIdData } = useClanMembers(
    {
      workerIds: [user._id],
      currentPage: 1,
      pageSize: 1,
      statuses: [ClanMemberStatus.ACTIVE],
    },
    {},
  );

  return clanIdData?.data[0]?.clanId;
};

export const useCreateClan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["createClan"],
    mutationFn: async (data: CreateClanRequest) => {
      const response = await apiInstance.post<
        ApiResponse<Clan>,
        { name: string }
      >(`/clans`, { name: data.name }, undefined, true);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clans"],
      });
      queryClient.invalidateQueries({
        queryKey: ["clanMembers"],
      });
    },
  });
};

export const useEditClanName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["editClanName"],
    mutationFn: async (data: EditClanRequest) => {
      const response = await apiInstance.patch<
        ApiResponse<Clan>,
        { name: string }
      >(`/clans/${data.clanId}/name`, { name: data.name }, undefined, true);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clans"],
      });
    },
  });
};

export const useKickClanMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kickClanMember"],
    mutationFn: async (data: KickClanMemberRequest) => {
      return await apiInstance.post(
        `/clans/${data.clanId}/members/${data.memberId}/kick`,
        undefined,
        undefined,
        true,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clanMembers"],
      });
    },
  });
};

export const useIsClanLeader = () => {
  const user = useAuthUser();
  const { data: clanMembers } = useClanMembers(
    {
      currentPage: 1,
      pageSize: 1,
      workerIds: [user._id],
      statuses: [ClanMemberStatus.ACTIVE],
    },
    { enabled: true },
  );

  return (
    clanMembers &&
    clanMembers.data.length > 0 &&
    clanMembers.data[0].role === ClanRole.LEADER
  );
};

export const useIsClanMember = () => {
  const user = useAuthUser();
  const { data: clanMembers } = useClanMembers(
    {
      currentPage: 1,
      pageSize: 1,
      workerIds: [user._id],
      statuses: [ClanMemberStatus.ACTIVE],
    },
    { enabled: !!user._id },
  );

  return (
    clanMembers &&
    clanMembers.data.length > 0 &&
    clanMembers.data[0].role === ClanRole.MEMBER
  );
};
